import { forwardRef, useCallback } from 'react';
import { uniqueArrayOfSimpleValues } from '@/utils/helpers/array/unique';
import handleSort from '../../utils/helpers/array/sort';
import { ChipStack, IChipStackValue } from '../ChipStack';
import { IMultiSelectProps } from '../MultiSelect';
import { Select, ISelectProps } from '../Select';

export type IMultiChipSelect = Omit<ISelectProps<false>, 'value' | 'onChange' | 'ref'> &
    Pick<IMultiSelectProps, 'value' | 'onChange'>;

export const MultiChipSelect = forwardRef<HTMLDivElement, IMultiChipSelect>(function InnerMultiChipSelect(
    { disabled, label, name, options, readOnly, value = [], onChange, ...rest },
    ref
) {
    const handleAdd = useCallback(
        (id?: string | null) => {
            if (!id) {
                return;
            }

            const found = options.find((item) => item.id === id);

            if (!found) {
                return;
            }

            if (onChange) {
                onChange(uniqueArrayOfSimpleValues([...value, id]));
            }
        },
        [options, value, onChange]
    );
    const handleRemove = useCallback(
        (ids: IChipStackValue['id'][]) => {
            if (onChange) {
                onChange(uniqueArrayOfSimpleValues(ids));
            }
        },
        [onChange]
    );
    const sortedOptions = Array.from(options).sort((prev, next) => handleSort(prev.id, next.id));

    return (
        <>
            <Select
                {...rest}
                ref={ref}
                name={name}
                label={label || name}
                options={sortedOptions.filter((option) => !value.includes(option.id))}
                disabled={disabled}
                readOnly={readOnly}
                onChange={handleAdd}
            />
            <ChipStack
                name={name}
                options={sortedOptions.filter((option) => value.includes(option.id))}
                disabled={disabled}
                readOnly={readOnly}
                sx={
                    value?.length > 0
                        ? {
                              padding: '1rem 0',
                              flexDirection: 'row',
                              flexWrap: 'wrap',
                              justifyContent: 'center',
                              gap: 1
                          }
                        : {}
                }
                onChange={handleRemove}
            />
        </>
    );
});
